import { create, tsx } from '@dojo/framework/core/vdom';
import { Summary } from './Summary';
import * as commonCss from '../Common.m.css';

const sitepenLogo = require('../assets/images/logos/sitepen.svg');
const images = {
	code: require('../assets/images/development/code-reuse.svg'),
	consulting: require('../assets/images/services/consulting.svg'),
	engineering: require('../assets/images/development/engineering.svg')
};

export interface WhySitePenProperties {
	image?: keyof typeof images;
}

const factory = create().properties<WhySitePenProperties>();

export const WhySitePen = factory(function WhySitePen({ properties }) {
	const { image = 'code' } = properties();

	return (
		<section classes={[commonCss.section, commonCss.contentWrapper]}>
			<Summary smaller collapse="large">
				{{
					title: 'Why Develop with SitePen',
					blurb: (
						<virtual>
							<p>
								At SitePen, we believe that quality is the single, most important
								aspect of any project we undertake. Our commitment to delivering
								bug-free solutions infuses every part of our development activities.
							</p>
							<p>
								You will start to experience this as soon as you reach out to us.
								Our Sales and Project Management teams work with you to understand
								your goals and expectations, identifying the characteristics that
								have the most significant influence on the application's quality.
							</p>
							<p>
								Our engineering team will then develop the solution with these goals
								in mind. They also create extensive suites of automated tests that
								ensure that the code continues to work as intended. Finally, we
								continuously monitor the project in search of refactoring
								opportunities to improve the code's structure as it evolves. While
								not usually considered an essential aspect of quality assurance, we
								have found that superior code organization often leads to
								exceptional quality in the final product.
							</p>
						</virtual>
					),
					image: (
						<img
							classes={[commonCss.responsiveImage]}
							src={sitepenLogo}
							alt=""
							loading="lazy"
						/>
					)
				}}
			</Summary>
			<Summary smaller reverse collapse="large" margin={false}>
				{{
					title: '',
					blurb: (
						<virtual>
							<p>
								As the application approaches completion, we will shift the project
								to examine the quality of the solution. During this phase, we
								conduct tests that exercise all aspects of the complete solution. We
								verify the business logic's correctness, ensure the code is
								performant and will not bog down under real-world workloads, and
								harden the application against the vast array of security threats
								that today's applications face.
							</p>
							<p>
								Naturally, the final decision concerning the quality of the
								application resides with you. We will work with your infrastructure
								team to deploy the solution to a staging environment that allows you
								to conduct your own quality assurance verification. When all
								findings have been identified, we will resolve them, ensuring that
								you receive the most reliable and accurate application possible.
							</p>
						</virtual>
					),
					image: (
						<img
							classes={commonCss.responsiveImage}
							src={images[image]}
							alt=""
							loading="lazy"
						/>
					)
				}}
			</Summary>
		</section>
	);
});

export default WhySitePen;
